import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutationMyBag_PromoCodeRemove_QueryKey } from "./data-mutation--mybag-promo-code-remove-query-key-factory";

export interface DataMutationMyBagPromoCodeRemoveConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationMyBagPromoCodeRemove_Result["data"]
  > {
  data: DataMutationMyBagPromoCodeRemove_Result["data"];
  request: DataMutationMyBagPromoCodeRemove_Result["variables"];
  status: DataMutationMyBagPromoCodeRemove_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagPromoCodeRemoveConsumerInterface;
const DataMutationMyBagPromoCodeRemoveContext =
  React.createContext(defaultValue);

export interface DataMutationMyBagPromoCodeRemove_ControllerInterface {
  mutate: DataMutationMyBagPromoCodeRemove_Result["mutate"];
  mutateAsync: DataMutationMyBagPromoCodeRemove_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagPromoCodeRemove_ControllerInterface;
const DataMutationMyBagPromoCodeRemove_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationMyBagPromoCodeRemove_Request {
  cartId: string;
}
export type DataMutationMyBagPromoCodeRemove_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationMyBagPromoCodeRemove_Request,
    unknown
  >;

export type GraphqlResponse_MyBagPromoCodeRemove = GraphqlWrappedData<
  "removeCouponFromCart",
  { cart: CheckoutModel_MyBag }
>;

export interface DataMutationMyBagPromoCodeRemoveProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagPromoCodeRemoveProvider: React.FunctionComponent<
  DataMutationMyBagPromoCodeRemoveProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_PromoCodeRemove_QueryKey.mybagPromoCodeRemove();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationMyBagPromoCodeRemove_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          removeCouponFromCart(
            input: {
              cart_id: "${request.cartId}"
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagPromoCodeRemove>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { removeCouponFromCart } = gqlResponseData || {};
      const { cart } = removeCouponFromCart || {};

      if ((window as any).getMiniCart as any) {
        ((window as any).getMiniCart as any)((customerData: any) => {
          const cart = customerData.get("cart");
          customerData.reload(["cart"], true);
        });
      }

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationMyBagPromoCodeRemoveConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        request: mutation.variables,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState: ignoreMyCurrentState,
      }),
      [
        mutation.data,
        mutation.variables,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState,
      ]
    );
  const controllerValue: DataMutationMyBagPromoCodeRemove_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagPromoCodeRemoveContext.Provider value={value}>
      <DataMutationMyBagPromoCodeRemove_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagPromoCodeRemove_ControllerContext.Provider>
    </DataMutationMyBagPromoCodeRemoveContext.Provider>
  );
};

export const useDataMutationMyBagPromoCodeRemove = () => {
  const context = React.useContext(DataMutationMyBagPromoCodeRemoveContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagPromoCodeRemove must be used within a DataMutationMyBagPromoCodeRemoveProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagPromoCodeRemove_asController = () => {
  const context = React.useContext(
    DataMutationMyBagPromoCodeRemove_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagPromoCodeRemove_asController must be used within a DataMutationMyBagPromoCodeRemoveProvider"
    );
  }
  return context;
};
